import { Card, CardHeader, CardTitle, CardToolbar } from '@/components/Card'
import Loading from '@/components/Loading'
import { groupByPagamento, type PagedFaturasResponse } from '@/services/fatura'
import type { Fatura } from '@/types/fatura'
import { Link } from 'react-router-dom'
import DetalhesPagamentoPago from './DetalhesPagamentoPago'
import FaturasPagasCard from './FaturasPagasCard'
import FaturaValorResumo from './FaturaValorResumo'
import './FaturaItem.scss'
import './FaturasView.scss'

interface FaturasPagasViewProps {
    faturasPage: PagedFaturasResponse<Fatura> | null
    isLoading: boolean
    backTo: string
    backLabel?: string
    onPageChange: (page: number) => void
}

export default function FaturasPagasView({
    faturasPage,
    isLoading,
    backTo,
    backLabel = 'Faturas em aberto',
    onPageChange,
}: FaturasPagasViewProps) {
    const faturas = faturasPage?.data ?? []
    const grupos = groupByPagamento(faturas)
    const currentPage = faturasPage?.current_page ?? 1
    const lastPage = faturasPage?.last_page ?? 1
    const total = faturasPage?.total ?? 0
    const from = faturasPage?.from ?? 0
    const to = faturasPage?.to ?? 0

    return (
        <div className="faturas-view">
            <div className="faturas-pagas-toolbar">
                <Link className="btn btn-outline-primary btn-sm" to={backTo}>
                    {backLabel}
                </Link>
                {!isLoading && (
                    <div className="faturas-pagas-count">
                        {total > 0 ? `${from}-${to} de ${total}` : 'Nenhuma fatura paga encontrada.'}
                    </div>
                )}
            </div>

            <section className="faturas-view-section" aria-label="Faturas pagas">
                <Card style={{ marginBottom: 12 }}>
                    <CardHeader>
                        <CardTitle>Faturas pagas</CardTitle>
                        {!isLoading && faturas.length > 0 && (
                            <CardToolbar>
                                <FaturaValorResumo faturas={faturas} variant="total-only" />
                            </CardToolbar>
                        )}
                    </CardHeader>
                </Card>

                {isLoading ? (
                    <Loading type="primary" />
                ) : faturas.length === 0 ? (
                    <p className="faturas-view-empty">Nenhuma fatura paga encontrada.</p>
                ) : (
                    grupos.map(grupo => {
                        const key = grupo[0].pagamento?.CdPagamento
                            ?? grupo[0].CdFatura
                        const forma = grupo[0].pagamento?.Forma
                            ?? grupo[0].FormaPagamento

                        return (
                            <div key={key} style={{ marginBottom: 16 }}>
                                {forma ? (
                                    <div className="faturas-pagas-group-label">
                                        {forma}
                                        {grupo.length > 1 ? ` · ${grupo.length} faturas` : ''}
                                    </div>
                                ) : null}
                                <div className="faturas-aberto-grid">
                                    <div className="faturas-aberto-list">
                                        <FaturasPagasCard faturas={grupo} />
                                    </div>
                                    <div className="faturas-aberto-pagamento">
                                        <DetalhesPagamentoPago faturas={grupo} />
                                    </div>
                                </div>
                            </div>
                        )
                    })
                )}
            </section>

            {(isLoading || lastPage > 1) && (
                <Card>
                    <CardHeader style={{ alignItems: 'center', padding: '12px 16px' }}>
                        <CardTitle style={{ margin: 0 }}>
                            Página {currentPage} de {lastPage}
                        </CardTitle>
                        <CardToolbar className="flex gap-2">
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={isLoading || currentPage <= 1}
                            >
                                Anterior
                            </button>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={isLoading || currentPage >= lastPage}
                            >
                                Próxima
                            </button>
                        </CardToolbar>
                    </CardHeader>
                </Card>
            )}
        </div>
    )
}
